import { Icon, Spinner, SpinnerSize, Tab } from "@blueprintjs/core";
import { useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { ERequestState, ERequestType, MiscellaneousRequestApi, RequestApi } from "../../../../api";
import { BackButton, PageBase, ProtectedTabs, RequestHistoryTab, RequestStickyMenu } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";
import { BillTab } from "../../BillTab";
import { MiscellaneousRequestDetail } from "./MiscellaneousRequestDetail";

export interface IMiscellaneousRequestItemPageProps {}

export const MiscellaneousRequestItemPage: React.FunctionComponent<IMiscellaneousRequestItemPageProps> = (props) => {
  const {
    id,
    tab,
    state,
    masterRequestId = "0",
  } = useParams<{
    id: string;
    tab: string;
    state: string;
    masterRequestId: string;
  }>();
  const history = useHistory();
  const api = useApiService(MiscellaneousRequestApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    () =>
      requestApi.getRequestInfo(ERequestType.MiscellaneousRequest, +id, +masterRequestId, currentLanguage.languageId),
    [requestApi, id, masterRequestId, currentLanguage],
  );

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
  }, [refreshRequestInfo]);

  const closeRequest = React.useCallback(async () => {
    await api.close(requestInfo?.parentRequestId);
    refreshRequestInfo();
  }, [api, refreshRequestInfo, requestInfo]);

  return (
    <PageBase
      breadCrumbs={[
        {
          text: t(ETLCodes.MiscellaneousRequest),
          route: ERoutes.miscellaneousRequest,
        },
        {
          text:
            +id > 0 ? (
              requestInfoLoading ? (
                <Spinner size={SpinnerSize.SMALL} />
              ) : (
                requestInfo?.requestNumber
              )
            ) : (
              t(ETLCodes.New)
            ),
        },
      ]}
      actionHistory={
        +id > 0 &&
        !requestInfoLoading && {
          tlCode: ETLCodes.ActionHistoryRsh,
          name: requestInfo?.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (requestInfo?.requestId > 0) {
          return (
            <RequestStickyMenu
              requestId={requestInfo?.parentRequestId}
              requestInfo={requestInfo}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsMiscellaneousRequest"
        onChange={(newTabId) => history.push(`${ERoutes.miscellaneousRequest}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.miscellaneousRequest} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={
            <MiscellaneousRequestDetail
              miscellaneousRequestId={+id}
              editMode={state === "edit"}
              requestInfo={requestInfo}
            />
          }
        />
        <Tab id="verification" title={t(ETLCodes.Verifications)} disabled />
        <Tab id="conditions" title={t(ETLCodes.Conditions)} disabled />
        <Tab id="subrequests" title={t(ETLCodes.SubRequests)} disabled />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
